/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {GraphQLClient} from 'graphql-request';
import isString from 'lodash.isstring';
import {
  IAsyncClientRegistration,
  setAsyncClientRegister
} from '@ion/core/src/services/AsyncClientManager';
import APM from '../APM';
import {IPerformanceMonitor} from '../types/types';
import getInitializationMonitor from './InitializationPerformanceMonitor';
import getPageMonitor from './PagePerformanceMonitor';

/**
 * @internal
 *
 * The `GraphQLMonitor` API provides an automated mechanism for monitoring
 * the requests and responses of GraphQL client instances in the context of
 * the Application Performance Monitoring API.
 *
 * When APM is enabled, clients can invoke the `register` method of the
 * `GraphQLMonitor` API to automatically intercept requests and responses
 * for performing timing metrics of GraphQL operations, allowing for automating
 * the process of measuring specific GraphQL operations as part of the APM
 * Framework's monitoring metrics.
 *
 * This API is primarily intended to be a package level instance, that is,
 * used internally by the underlying PerformanceMonitor instances.
 */
class GraphQLMonitor implements IAsyncClientRegistration {
  /**
   * Provides a reference to the GraphQL clients which are being observed
   * by all currently running Performance Monitors.
   */
  protected clients: Set<GraphQLClient>;

  /**
   * Provides a reference to the `PerformanceMonitor` instances for which
   * all intercepted GraphQL operations are to be measured.
   */
  protected monitors: IPerformanceMonitor[];

  /**
   * The `GraphQLMonitor` constructor initializes the collection of performance
   * monitors which are to automatically measure GraphQL operations. By default,
   * the `InitializationPerformanceMonitor` and `PagePerformanceMonitor` instances
   * are assigned to the monitors.
   */
  constructor() {
    this.clients = new Set();
    this.monitors = [
      getInitializationMonitor(),
      getPageMonitor()
    ];
  }

  /**
   * Determines the number of clients which have been registered and returns
   * the result.
   *
   * @returns the number of GraphQL clients that have been registered.
   */
  public getClientCount(): number {
    return this.clients?.size || 0;
  }

  /**
   * Registers a GraphQL client for request/response interception to be included
   * in the service metrics of all currently running Performance Monitors.
   *
   * @param client The GraphQL client which is to have its requests and responses
   *        intercepted for measurement within the APM Framework.
   */
  public register<T>(instance: T): void {
    const client = instance as unknown as GraphQLClient;
    const shouldRegister = !this.clients.has(client);
    if (shouldRegister) {
      this.clients.add(client);
      this.patchClientRequest(client);
    }
  }

  /**
   * Patches the GraphQL client's request method to monitor GraphQL operations.
   *
   * @param client The GraphQL client to patch.
   */
  protected patchClientRequest(client: GraphQLClient): void {
    const originalRequest = client.request;
    let requestGUID = 0;
    client.request = async(...args: any[]) => {
      const operationName = this.extractOperationName(args[0]);
      const requestId     = ++requestGUID;
      const servicePath   = `/graphql/${operationName || 'query'}#${requestId}`;
      this.monitors.forEach(monitor => {
        monitor.isRunning() && monitor.startService(servicePath);
      });
      try {
        return await originalRequest.apply(client, args);
      } finally {
        this.monitors.forEach(monitor => {
          monitor.isRunning() && monitor.stopService(servicePath);
        });
      }
    };
  }

  /**
   * Extracts the operation name from a GraphQL query or mutation document.
   * This is used to provide meaningful service paths for APM monitoring.
   *
   * @param query The GraphQL query document or string.
   * @returns The extracted operation name or undefined if not found.
   */
  protected extractOperationName(query: any): string | undefined {
    if (query) {
      if (isString(query)) {
        const match = query.match(/(?:query|mutation)\s+([\w_]+)/);
        return match?.[1];
      }
      return query?.definitions?.[0]?.name?.value;
    }
  }
}

/**
 * Provides a reference to the GraphQL client registration instance.
 */
let graphQLClientRegister: IAsyncClientRegistration;

/**
 * Sets the GraphQL client registration instance to be used for registering
 * GraphQL clients within the APM framework.
 *
 * @param register The GraphQL client registration instance.
 */
export const setGraphQLClientRegister = (register: IAsyncClientRegistration): void => {
  graphQLClientRegister = register;
};

/**
 * Registers a GraphQL client with the APM framework for performance monitoring.
 *
 * @param client The GraphQL client which is to be registered for observability.
 */
export const registerGraphQLClient = (client: any): void => {
  graphQLClientRegister?.register(client);
};

/**
 * Provides the single point of entry for referencing the `GraphQLMonitor` instance.
 * Note, this is a pseudo-internal API (package-level in the traditional sense) in
 * that the creation of the `GraphQLMonitor` instance is to be exclusively managed
 * by a single API.
 */
const initializeGraphQLMonitor = (() => {
  let instance: IAsyncClientRegistration;
  return (): IAsyncClientRegistration => {
    if (APM.isEnabled) {
      if (!instance) {
        instance = new GraphQLMonitor();
        setAsyncClientRegister(instance);
        setGraphQLClientRegister(instance);
      }
    } else {
      instance = undefined;
    }
    return instance;
  };
})();

export default initializeGraphQLMonitor;
